import React from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import VenueCard from '../VenueCard';
import PlatinumVenueCard from '../PlatinumVenueCard';
import SeeAllPlatinumNightsCard from '../SeeAllPlatinumNightsCard';
import Carousel from '../Carousel';
import Link from '../Link';
import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';

import './VenueCardGrid.scss';

const emptyVenue = {
  venue: {
    location: {},
  },
};
const BOOK_TONIGHT_UI_LIST_ID = 14;

const getPlatinumNightInfo = (restaurant, key) => {
  if (!restaurant.venue || !restaurant.venue?.[key]) {
    return null;
  }
  return restaurant.venue?.[key];
};

const renderRestaurant = ({
  $rootScope,
  loadNotifyModal,
  restaurant,
  event,
  favoriteVenueIds,
  isAmexShopSmallBuyoutFlag,
  isAmexShopSmallFlag,
  isAmexShopSmallOffersFlag,
  isDeltaWifi,
  isPlatinum,
  list,
  selectedDate,
  selectedSeats,
  selectedTime,
  shopSmallBuyoutVenueCardEvents,
  shopSmallVenueCardEvents,
  showAuthModal,
  userIsGda,
  tagBooking,
  amexVenueCardEvents,
  amexContextName,
  taggingConfig,
  isAmexGDA,
  isAmexMktgResyCreditEligible,
}) => {
  const RenderedVenueCard = isPlatinum ? PlatinumVenueCard : VenueCard;
  const selectedDateForCard = isPlatinum ? getPlatinumNightInfo(restaurant, 'platinumNightDate') : selectedDate;
  const platinumInventoryStatus = isPlatinum ? getPlatinumNightInfo(restaurant, 'platinumNightStatus') : null;
  return (
    <RenderedVenueCard
      $rootScope={$rootScope}
      event={event}
      list={list}
      selectedDate={selectedDateForCard}
      selectedSeats={selectedSeats}
      selectedTime={selectedTime}
      activeNotifications={restaurant.venue.activeNotifications}
      availableServiceTypes={restaurant.venue.availableServiceTypes}
      cuisine={restaurant.venue.cuisine}
      events={restaurant.venue.events}
      featureRecaptcha={restaurant.venue.featureRecaptcha}
      gdaConciergeBooking={restaurant.venue.gdaConciergeBooking}
      image={restaurant.venue.images?.length > 0 ? restaurant.venue.images[0] : null}
      isInventoryComingSoon={platinumInventoryStatus === 'coming soon'}
      loadNotifyModal={loadNotifyModal}
      locationCode={restaurant.venue.location.code}
      locationName={restaurant.venue.location.name}
      locationSlug={restaurant.venue.location.urlSlug}
      name={restaurant.venue.name}
      neighborhood={restaurant.venue.location.neighborhood}
      priceRange={restaurant.venue.priceRange}
      currency={restaurant.venue.currency}
      rating={restaurant.venue.rating}
      totalRatings={restaurant.venue.totalRatings}
      reservations={restaurant.slots}
      urlSlug={restaurant.venue.urlSlug}
      venueId={restaurant.venue.id}
      isAmexMktgResyCreditEligible={isAmexMktgResyCreditEligible}
      tockBusinessPageUrl={restaurant.venue.tockBusinessPageUrl}
      isTockInventory={restaurant.venue.isTockInventory}
      tockBusinessSearchUrl={restaurant.venue.tockBusinessSearchUrl}
      tockNotifyUrl={restaurant.venue.tockNotifyUrl}
      tockExperiences={restaurant.venue.experiences}
      key={restaurant.venue.id}
      userIsGda={userIsGda}
      isDeltaWifi={isDeltaWifi}
      tagBooking={tagBooking}
      isAmexShopSmallFlag={isAmexShopSmallFlag}
      isAmexShopSmallBuyoutFlag={isAmexShopSmallBuyoutFlag}
      isAmexShopSmallOffersFlag={isAmexShopSmallOffersFlag}
      shopSmallVenueCardEvents={shopSmallVenueCardEvents}
      shopSmallNofityMessage={restaurant.shopSmallNotifyDropMessage}
      shopSmallBuyoutState={restaurant.shopSmallBuyoutState}
      shopSmallBuyoutEnabled={restaurant.shopSmallBuyoutEnabled}
      shopSmallBuyoutVenueCardEvents={shopSmallBuyoutVenueCardEvents}
      showAuthModal={showAuthModal}
      favoriteVenueIds={favoriteVenueIds}
      favorite={restaurant.venue.favorite}
      amexVenueCardEvents={amexVenueCardEvents}
      amexContextName={amexContextName}
      taggingConfig={taggingConfig}
      isAmexGDA={isAmexGDA}
    />
  );
};

const VenueCardGrid = ({
  $rootScope,
  description,
  event,
  handleSeeMoreClick,
  isMobile,
  link,
  list,
  listId,
  loadNotifyModal,
  matchMedia,
  restaurants,
  selectedDate,
  selectedSeats,
  selectedTime,
  userIsGda,
  isDeltaWifi,
  isPlatinum,
  deltaWifiVenuelength,
  tagBooking,
  isAmexShopSmallFlag,
  isAmexShopSmallBuyoutFlag,
  isAmexShopSmallOffersFlag,
  shopSmallVenueCardEvents,
  shopSmallBuyoutVenueCardEvents,
  showAuthModal,
  showCarouselButtons,
  favoriteVenueIds,
  amexVenueCardEvents,
  amexContextName,
  taggingConfig,
  isAmexGDA,
  isAmexMktgResyCreditEligible,
}) => {
  const handleCarouselArrowClick = (direction) => {
    event('click collection carousel arrow', {
      direction,
      list,
      list_id: listId,
    });
  };

  const renderSeeAllLink = () => {
    if (isPlatinum) {
      return (
        <TranslationProvider translationKey="see_all_label_platinum">
          <span>See All Platinum Nights&nbsp;&rsaquo;</span>
        </TranslationProvider>
      );
    }

    return (
      <TranslationProvider translationKey="see_all_label">
        <span>See All&nbsp;&rsaquo;</span>
      </TranslationProvider>
    );
  };

  const renderSeeMoreCard = () => {
    let seeAllLink = link;

    if (isPlatinum && !isMobile) {
      return (
        <SeeAllPlatinumNightsCard
          image="https://s3.amazonaws.com/resy.com/images/platinum-see-all-cards.jpg"
          name={getTranslation({
            translationKey: 'platinum_default_card_description',
            fallback: 'Tables for American Express Platinum Card® Members in NYC, LA, and MIA.',
          })}
        />
      );
    }

    if (isPlatinum && isMobile) {
      seeAllLink = null;
    }

    return (
      <Link
        className="VenueCardGrid__link"
        href={seeAllLink}
        onClick={handleSeeMoreClick}
        aria-label={getTranslation({
          translationKey: 'see_all',
          fallback: 'See All',
        })}
      >
        <div className="VenueCard">
          <div className={cx('VenueCardGrid__see-all', {
            'VenueCardGrid__see-all--platinum': isPlatinum,
          })}
          >
            {renderSeeAllLink()}
          </div>
        </div>
      </Link>
    );
  };

  const getSeeAllSlide = (slideContent) => {
    const label = getTranslation({
      translationKey: 'see_all_card',
      fallback: 'See all venues in this collection',
    });

    const seeAllCard = {
      card: renderSeeMoreCard(),
      label,
    };

    if (isMobile && (slideContent.length > 4 || isPlatinum)) {
      return seeAllCard;
    } if (!isMobile && slideContent.length < 4 && isPlatinum) {
      return seeAllCard;
    }

    return null;
  };

  const renderVenueGrid = () => {
    let buttonStyles = { top: '3px' };
    let cardsToRender;
    let slideContent = null;

    slideContent = restaurants?.map((restaurant) => ({
      card: renderRestaurant({
        $rootScope,
        event,
        list,
        loadNotifyModal,
        restaurant,
        selectedDate,
        selectedSeats,
        selectedTime,
        userIsGda,
        isDeltaWifi,
        isPlatinum,
        tagBooking,
        isAmexShopSmallFlag,
        isAmexShopSmallBuyoutFlag,
        isAmexShopSmallOffersFlag,
        shopSmallVenueCardEvents,
        shopSmallBuyoutVenueCardEvents,
        showAuthModal,
        favoriteVenueIds,
        amexVenueCardEvents,
        amexContextName,
        taggingConfig,
        isAmexGDA,
        isAmexMktgResyCreditEligible,
      }),
      label: restaurant.venue.name,
    }));

    const seeAllSlide = getSeeAllSlide(slideContent);
    if (seeAllSlide) slideContent.push(seeAllSlide);

    if (listId === BOOK_TONIGHT_UI_LIST_ID) {
      buttonStyles = {
        top: '55px',
      };
    } else if (isPlatinum) {
      buttonStyles = {
        top: '48px',
      };
    } else if (description) {
      buttonStyles = {
        top: '27px',
      };
    }

    if (matchMedia.xs) { cardsToRender = 1.25; }
    if (matchMedia.sm) { cardsToRender = 2; }
    if (matchMedia.md) { cardsToRender = 3; }
    if (matchMedia.lg) { cardsToRender = isPlatinum ? 3 : 4; }
    if (matchMedia.xl) { cardsToRender = isPlatinum ? 4 : 5; }

    return (
      <Carousel
        step={cardsToRender}
        carouselId="collection"
        visibleSlides={cardsToRender}
        slideContent={slideContent}
        onArrowClick={handleCarouselArrowClick}
        hasButtons={!isMobile && showCarouselButtons}
        buttonPosition="top-right"
        buttonStyles={buttonStyles}
        infinite={false}
        isMobile={isMobile}
        isIntrinsicHeight
        touchEnabled
        preventVerticalScrollOnTouch

      />
    );
  };

  if (!isDeltaWifi) {
    return renderVenueGrid();
  }

  return (
    <div className={`DeltaWifi__venuegrid card-${deltaWifiVenuelength}-list`}>
      {renderVenueGrid()}
    </div>
  );
};

VenueCardGrid.propTypes = {
  $rootScope: PropTypes.shape({
    $broadcast: PropTypes.func.isRequired,
  }).isRequired,
  description: PropTypes.shape({}),
  event: PropTypes.func.isRequired,
  handleSeeMoreClick: PropTypes.func.isRequired,
  link: PropTypes.string.isRequired,
  list: PropTypes.string,
  listId: PropTypes.number,
  loadNotifyModal: PropTypes.func.isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  restaurants: PropTypes.arrayOf(PropTypes.shape({
    venue: PropTypes.shape({
      priceRange: PropTypes.number,
      rating: PropTypes.number,
      name: PropTypes.string,
      images: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
  })),
  selectedDate: PropTypes.string.isRequired,
  selectedSeats: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selectedTime: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  showCarouselButtons: PropTypes.bool.isRequired,
  userIsGda: PropTypes.bool,
  deltaWifiVenuelength: PropTypes.number,
  isDeltaWifi: PropTypes.bool,
  tagBooking: PropTypes.shape({
    utm: PropTypes.shape({}),
  }),
  isAmexShopSmallFlag: PropTypes.bool,
  isAmexShopSmallBuyoutFlag: PropTypes.bool,
  isAmexShopSmallOffersFlag: PropTypes.bool,
  isMobile: PropTypes.bool.isRequired,
  isPlatinum: PropTypes.bool,
  shopSmallVenueCardEvents: PropTypes.shape({
    view_more: PropTypes.string,
    book_now: PropTypes.string,
    notify: PropTypes.string,
    venue_click: PropTypes.string,
    venue_utm: PropTypes.shape({
      utm_source: PropTypes.string,
      utm_medium: PropTypes.string,
      utm_campaign: PropTypes.string,
    }),
  }),
  amexVenueCardEvents: PropTypes.shape({
    view_more: PropTypes.string,
    book_now: PropTypes.string,
    notify: PropTypes.string,
    venue_click: PropTypes.string,
    venue_utm: PropTypes.shape({
      utm_source: PropTypes.string,
      utm_medium: PropTypes.string,
      utm_campaign: PropTypes.string,
    }),
  }),
  shopSmallBuyoutVenueCardEvents: PropTypes.shape({
    view_more: PropTypes.string,
    book_now: PropTypes.string,
    notify: PropTypes.string,
    venue_click: PropTypes.string,
  }),
  showAuthModal: PropTypes.func.isRequired,
  favoriteVenueIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  amexContextName: PropTypes.string,
  taggingConfig: PropTypes.shape({}),
  isAmexGDA: PropTypes.bool,
  isAmexMktgResyCreditEligible: PropTypes.bool,
};

VenueCardGrid.defaultProps = {
  description: undefined,
  list: '',
  listId: null,
  restaurants: [emptyVenue, emptyVenue, emptyVenue, emptyVenue, emptyVenue],
  selectedSeats: 2,
  selectedTime: undefined,
  userIsGda: false,
  deltaWifiVenuelength: 0,
  isDeltaWifi: false,
  isPlatinum: false,
  tagBooking: {},
  isAmexShopSmallFlag: false,
  isAmexShopSmallBuyoutFlag: false,
  shopSmallVenueCardEvents: {},
  isAmexShopSmallOffersFlag: false,
  shopSmallBuyoutVenueCardEvents: {},
  amexVenueCardEvents: {},
  amexContextName: '',
  taggingConfig: {},
  isAmexGDA: false,
  isAmexMktgResyCreditEligible: false,
};

VenueCardGrid.displayName = 'VenueCardGrid';
export default VenueCardGrid;
